import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { EmailOtpType } from '@supabase/supabase-js';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import idseatLogo from '@/assets/idseat-logo.svg';

/**
 * Where every auth email lands (invite, password reset, magic link, sign-up and
 * email-change confirmations): OUR domain, with a token hash in the query —
 * the templates in supabase/templates link `{{ .RedirectTo }}?token_hash=…&type=…`.
 * The one-time token is exchanged only when the person clicks Continue, so a
 * mail gateway that prefetches links cannot consume it, and the Supabase
 * project URL never appears in a mail. After the exchange the session exists
 * and the person goes on to set a password (invite, recovery) or straight in.
 *
 * Legacy landing: a mail sent with the old `{{ .ConfirmationURL }}` template
 * goes through GoTrue's verify URL first, which exchanges the token itself and
 * redirects here with the session in the URL hash (`#access_token=…&type=…`,
 * or `#error=…` once used). supabase-js picks that session up on load; this
 * page waits for it and carries on the same way.
 */
const FLOWS: Record<string, { title: string; description: string; next: string }> = {
  invite: { title: "You're invited to IDSeat", description: 'Continue to set your password and open your workspace.', next: '/reset-password' },
  recovery: { title: 'Reset your password', description: 'Continue to choose a new password.', next: '/reset-password' },
  magiclink: { title: 'Sign in to IDSeat', description: 'Continue to open IDSeat.', next: '/' },
  signup: { title: 'Confirm your account', description: 'Continue to confirm your email and open IDSeat.', next: '/' },
  email_change: { title: 'Confirm your new email', description: 'Continue to confirm the change.', next: '/' },
};

type Status = 'idle' | 'working' | 'expired';
const SESSION_PROBES = 10;
const SESSION_PROBE_MS = 300;

export default function AuthConfirm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tokenHash = params.get('token_hash') ?? '';
  // The hash is not routing state: read it from the window, once.
  const legacy = useMemo(() => new URLSearchParams((typeof window !== 'undefined' ? window.location.hash : '').replace(/^#/, '')), []);
  const legacySession = legacy.has('access_token');
  const legacyError = legacy.has('error') || legacy.has('error_code');
  const type = params.get('type') || legacy.get('type') || '';
  const flow = FLOWS[type];
  const [status, setStatus] = useState<Status>(legacyError ? 'expired' : legacySession ? 'working' : 'idle');
  const incomplete = !legacySession && !legacyError && (!flow || !tokenHash);

  // Legacy landing: wait for supabase-js to adopt the session from the hash.
  useEffect(() => {
    if (!legacySession) return;
    let cancelled = false;
    const next = flow?.next ?? '/';
    const probe = async (attempt: number) => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session) { navigate(next, { replace: true }); return; }
      if (attempt < SESSION_PROBES) setTimeout(() => void probe(attempt + 1), SESSION_PROBE_MS);
      else setStatus('expired');
    };
    void probe(0);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacySession]);

  const confirm = async () => {
    if (!flow) return;
    setStatus('working');
    const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: type as EmailOtpType });
    if (error) {
      setStatus('expired');
      return;
    }
    navigate(flow.next, { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-border shadow-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={idseatLogo} alt="IDSeat" className="h-12" />
          </div>
          <CardTitle className="text-2xl">{flow?.title ?? 'IDSeat'}</CardTitle>
          {flow && !incomplete && status === 'idle' && <CardDescription>{flow.description}</CardDescription>}
        </CardHeader>
        <CardContent>
          {incomplete ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <p className="text-sm text-muted-foreground">
                This link is incomplete. Open the link from the email again, or ask for a new one.
              </p>
              <Button className="w-full" onClick={() => navigate('/login')}>Back to sign in</Button>
            </div>
          ) : status === 'expired' ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <p className="text-sm text-muted-foreground">
                This link has expired or was already used. Links are single-use and time-limited — ask for a new one
                {type === 'invite' ? ' from the person who invited you.' : ' from the sign-in page.'}
              </p>
              <Button className="w-full" onClick={() => navigate('/login')}>Back to sign in</Button>
            </div>
          ) : legacySession ? (
            <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-sm">Signing you in…</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <Button className="w-full" onClick={confirm} disabled={status === 'working'}>
                {status === 'working' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Continue
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
